import React from 'react';

import Form, { useForm } from 'src/presentation/components/forms/Form';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import { NumberPreference } from 'src/presentation/components/pages/channels/shared/helpers/stageConfiguration';
import {
  StepWizardActionContainer,
  StepWizardFieldWithBorder,
} from 'src/presentation/components/pages/email/components/StepWizardLayout';
import { StyledRadioGroup } from './shared/elements';
import { useAvailableNumbers } from 'src/presentation/components/pages/channels/shared/hooks/useAvailableNumbers';
import {
  useFormChange,
  useResetFormState,
} from 'src/presentation/components/pages/channels/shared/hooks/useFormChange';
import { PhoneNumberCapability } from 'src/models/CompanyPhoneNumber/CompanyPhoneNumber';

const VoiceOptions = (availableNumbersLength: number) => ({
  GET_NEW_PHONE_NUMBER: {
    label: 'Get a new phone number',
    value: NumberPreference.new,
    description: 'Buy and manage a new number via Gladly.',
  },
  PORT_PHONE_NUMBER_FROM_ANOTHER_PROVIDER: {
    label: 'Port or forward a phone number from another provider',
    value: NumberPreference.port,
    description: `Use an existing number you already own for voice calls in Gladly.`,
  },
  USE_EXISTING_PHONE_NUMBER: {
    label: 'Use an existing Gladly phone number',
    value: NumberPreference.exists,
    description: `Set up a number you already own and has been added to Gladly, but has not yet been configured for voice. ${availableNumbersLength} number(s) available.`,
  },
});

const SmsOptions = (availableNumbersLength: number) => ({
  GET_NEW_PHONE_NUMBER: {
    label: 'Get a new phone number',
    value: NumberPreference.new,
    description: 'Buy and manage a new number via Gladly.',
  },
  PORT_PHONE_NUMBER_FROM_ANOTHER_PROVIDER: {
    label: 'Port a phone number from another provider',
    value: NumberPreference.port,
    description: 'Use an existing number you already own for SMS messages in Gladly.',
  },
  USE_EXISTING_PHONE_NUMBER: {
    label: 'Use an existing Gladly phone number',
    value: NumberPreference.exists,
    description: (
      <>
        Set up a number you already own and has been added to Gladly, but has not yet been configured for SMS.
        <br /> {availableNumbersLength} number(s) available.
      </>
    ),
  },
});

const getPhonePreferences = (entityType: PhoneNumberCapability, availableNumbersLength: number) => {
  const options =
    entityType === PhoneNumberCapability.VOICE
      ? VoiceOptions(availableNumbersLength)
      : SmsOptions(availableNumbersLength);

  if (availableNumbersLength === 0) {
    return [options.GET_NEW_PHONE_NUMBER, options.PORT_PHONE_NUMBER_FROM_ANOTHER_PROVIDER];
  }

  return [
    options.GET_NEW_PHONE_NUMBER,
    options.USE_EXISTING_PHONE_NUMBER,
    options.PORT_PHONE_NUMBER_FROM_ANOTHER_PROVIDER,
  ];
};

const initialWorkingState = { numberPreference: NumberPreference.new };

const SelectPhonePreference = ({
  nextStep,
  saveStep,
  entityType,
  stageConfig,
}: {
  nextStep?;
  saveStep?;
  entityType?: PhoneNumberCapability;
  stageConfig: any;
}): JSX.Element | null => {
  const formContext = useForm({
    item: initialWorkingState,
    resetErrors: [],
  });

  useFormChange(formContext.workingState, saveStep);
  useResetFormState(initialWorkingState, formContext.updateState, stageConfig, entityType);

  const { availableNumbers } = useAvailableNumbers(entityType);
  const availableNumbersLength = availableNumbers.length;

  return (
    <>
      <StepWizardFieldWithBorder>
        <H3 data-aid="wizard-title">Select your phone number preference</H3>
        <Form context={formContext}>
          <StyledRadioGroup
            className="radioGroup"
            attr="numberPreference"
            data-aid="numberPreference-radio-group"
            options={getPhonePreferences(entityType!, availableNumbersLength)}
          />
        </Form>
      </StepWizardFieldWithBorder>
      <StepWizardActionContainer>
        <Button data-aid="editor-save" type={Button.Types.PRIMARY} onClick={nextStep}>
          Next
        </Button>
      </StepWizardActionContainer>
    </>
  );
};

export default SelectPhonePreference;
